import Phaser from 'phaser';

/**
 * MobileControls - Virtual joystick and action buttons for touch devices
 * 
 * Adds:
 * - Left side: Virtual joystick for movement
 * - Right side: Action buttons (Jump, Dash, Attack, Interact)
 */

interface JoystickState {
  isDown: boolean;
  angle: number;
  force: number;
  x: number;
  y: number;
}

interface ButtonState {
  isDown: boolean;
  justPressed: boolean;
}

export class MobileControls {
  private scene: Phaser.Scene;
  private container!: Phaser.GameObjects.Container;
  
  // Joystick
  private joystickBase!: Phaser.GameObjects.Arc;
  private joystickThumb!: Phaser.GameObjects.Arc;
  private joystickPointer: Phaser.Input.Pointer | null = null;
  private joystickBaseX: number = 0;
  private joystickBaseY: number = 0;
  
  // Buttons
  private buttons: Map<string, Phaser.GameObjects.Arc> = new Map();
  private buttonStates: Map<string, ButtonState> = new Map();
  
  // State
  public joystick: JoystickState = {
    isDown: false,
    angle: 0,
    force: 0,
    x: 0,
    y: 0,
  };
  
  // Config
  private joystickRadius = 50;
  private thumbRadius = 25;
  private buttonRadius = 30;
  private alpha = 0.6;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.create();
  }

  private create() {
    const { width, height } = this.scene.scale;
    
    // Only show on touch devices or small screens
    if (!this.scene.sys.game.device.input.touch && width > 800) {
      return;
    }

    this.container = this.scene.add.container(0, 0);
    this.container.setDepth(1000);
    this.container.setScrollFactor(0);

    // Create joystick (left side)
    this.createJoystick(100, height - 120);

    // Create action buttons (right side)
    this.createActionButtons(width - 80, height - 120);

    // Setup input handling
    this.setupInput();
  }

  private createJoystick(x: number, y: number) {
    this.joystickBaseX = x;
    this.joystickBaseY = y;

    // Base circle
    this.joystickBase = this.scene.add.circle(x, y, this.joystickRadius, 0x000000, this.alpha);
    this.joystickBase.setStrokeStyle(3, 0xffffff, this.alpha);
    this.container.add(this.joystickBase);

    // Thumb circle
    this.joystickThumb = this.scene.add.circle(x, y, this.thumbRadius, 0xffffff, this.alpha);
    this.container.add(this.joystickThumb);

    // Direction indicators
    const dirs = [
      { dx: 0, dy: -1, label: '▲' },
      { dx: 0, dy: 1, label: '▼' },
      { dx: -1, dy: 0, label: '◀' },
      { dx: 1, dy: 0, label: '▶' },
    ];

    dirs.forEach(dir => {
      const indicator = this.scene.add.text(
        x + dir.dx * (this.joystickRadius + 15),
        y + dir.dy * (this.joystickRadius + 15),
        dir.label,
        { fontSize: '14px', color: '#ffffff' }
      );
      indicator.setOrigin(0.5);
      indicator.setAlpha(0.4);
      this.container.add(indicator);
    });
  }

  private createActionButtons(x: number, y: number) {
    // Button layout (diamond pattern)
    const buttonConfig = [
      { id: 'jump', label: '⬆', x: 0, y: -50, color: 0x22c55e },      // Top - Jump
      { id: 'attack', label: '⚔', x: 0, y: 50, color: 0xef4444 },     // Bottom - Attack
      { id: 'dash', label: '💨', x: -50, y: 0, color: 0x3b82f6 },     // Left - Dash
      { id: 'interact', label: 'E', x: 50, y: 0, color: 0xfbbf24 },   // Right - Interact
    ];

    buttonConfig.forEach(btn => {
      const button = this.scene.add.circle(
        x + btn.x,
        y + btn.y,
        this.buttonRadius,
        btn.color,
        this.alpha
      );
      button.setStrokeStyle(2, 0xffffff, this.alpha);
      button.setInteractive();
      this.container.add(button);
      this.buttons.set(btn.id, button);
      this.buttonStates.set(btn.id, { isDown: false, justPressed: false });

      // Label
      const label = this.scene.add.text(x + btn.x, y + btn.y, btn.label, {
        fontSize: '18px',
        color: '#ffffff',
      });
      label.setOrigin(0.5);
      this.container.add(label);

      // Touch events
      button.on('pointerdown', () => {
        const state = this.buttonStates.get(btn.id)!;
        state.isDown = true;
        state.justPressed = true;
        button.setScale(0.9);
        this.emitButtonEvent(btn.id, 'down');
      });

      button.on('pointerup', () => {
        const state = this.buttonStates.get(btn.id)!;
        state.isDown = false;
        button.setScale(1);
        this.emitButtonEvent(btn.id, 'up');
      });

      button.on('pointerout', () => {
        const state = this.buttonStates.get(btn.id)!;
        state.isDown = false;
        button.setScale(1);
      });
    });
  }

  private setupInput() {
    // Joystick touch handling
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Check if touch is in joystick area (left third of screen)
      if (pointer.x < this.scene.scale.width / 3 && !this.joystickPointer) {
        this.joystickPointer = pointer;
        this.joystick.isDown = true;
        this.updateJoystick(pointer);
      }
    });

    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.joystickPointer && pointer.id === this.joystickPointer.id) {
        this.updateJoystick(pointer);
      }
    });

    this.scene.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (this.joystickPointer && pointer.id === this.joystickPointer.id) {
        this.resetJoystick();
      }
    });
  }

  private updateJoystick(pointer: Phaser.Input.Pointer) {
    const dx = pointer.x - this.joystickBaseX;
    const dy = pointer.y - this.joystickBaseY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const maxDistance = this.joystickRadius;

    // Calculate force (0-1)
    this.joystick.force = Math.min(distance / maxDistance, 1);
    
    // Calculate angle
    this.joystick.angle = Math.atan2(dy, dx);

    // Normalized direction
    if (distance > 0) {
      this.joystick.x = dx / Math.max(distance, maxDistance);
      this.joystick.y = dy / Math.max(distance, maxDistance);
    }

    // Move thumb (constrained to base)
    const thumbX = this.joystickBaseX + (dx / Math.max(distance, 1)) * Math.min(distance, maxDistance);
    const thumbY = this.joystickBaseY + (dy / Math.max(distance, 1)) * Math.min(distance, maxDistance);
    this.joystickThumb.setPosition(thumbX, thumbY);
  }

  private resetJoystick() {
    this.joystickPointer = null;
    this.joystick.isDown = false;
    this.joystick.force = 0;
    this.joystick.x = 0;
    this.joystick.y = 0;
    this.joystickThumb.setPosition(this.joystickBaseX, this.joystickBaseY);
  }

  private emitButtonEvent(buttonId: string, eventType: 'down' | 'up') {
    // Emit custom event that scenes can listen to
    this.scene.events.emit(`mobile-${buttonId}-${eventType}`);
  }

  // Public API
  isButtonDown(buttonId: string): boolean {
    return this.buttonStates.get(buttonId)?.isDown || false;
  }

  isButtonJustPressed(buttonId: string): boolean {
    const state = this.buttonStates.get(buttonId);
    if (state?.justPressed) {
      state.justPressed = false;
      return true;
    }
    return false;
  }

  getMovementVector(): { x: number; y: number } {
    return { x: this.joystick.x, y: this.joystick.y };
  }

  update() {
    // Reset justPressed flags
    this.buttonStates.forEach(state => {
      state.justPressed = false;
    });
  }

  destroy() {
    if (this.container) {
      this.container.destroy();
    }
  }

  // Check if controls should be visible
  static shouldShow(): boolean {
    // Show on touch devices or narrow screens
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || window.innerWidth < 800;
  }
}
